import React, {
	useRef,
	Fragment,
	useState,
	useMemo,
	useCallback,
	useEffect
} from 'react';
import Modal from '../../../../common/components/Modal/Modal';
import {labels} from '../../../../common/utils/utils';
import styled from 'styled-components';
import {useDispatch} from 'react-redux';
import {createDelegate, getUserById, removeDelegateUser} from '../../users';
import AddDelegate from '../DelegateAccess/AddDelegate/AddDelegate';
import {MotifProgressLoader} from '@ey-xd/motif-react';
import {useSelector} from 'react-redux';
import TabBar from '../../../../common/components/TabBar/TabBar';
import ViewDelegate from './ViewDelegate/ViewDelegate';

function DelegateAccessModal(props) {
	const {showModal, setModalVisibility} = props;
	const dispatch = useDispatch();
	const user = useSelector((store) => store.user);

	const tabs = useMemo(() => {
		return [
			{
				id: '1',
				disabled: false,
				label: labels.delegateTo,
				value: 'delegatedTo',
				variant: 'primary'
			},
			{
				id: '2',
				disabled: false,
				label: labels.delegateFrom,
				value: 'delegatedFrom',
				variant: 'primary'
			}
		];
	}, [labels.delegateTo, labels.delegateFrom]);

	const [isLoading, setLoading] = useState(false);
	const [isDisable, setIsDisable] = useState(true);
	const [activeTab, setActiveTab] = useState('1');
	const [users, setUsers] = useState(user.delegatedTo);
	const [noDelegationMessage, setNoDelegationMessage] = useState(
		labels.noDelegatesAdded
	);

	const modalRef = useRef();

	const handleSaveDelegate = useCallback(
		(closeModal) => {
			if (activeTab !== '1') {
				return;
			}
			const isError = modalRef.current.validate();
			if (!isError) {
				setLoading(true);
				const users = modalRef.current.getUsers();
				if (users && users.length) {
					saveDelegate(users, closeModal);
				}
			}
		},
		[activeTab, modalRef, saveDelegate]
	);

	const saveDelegate = (users, closeModal = false) => {
		dispatch(createDelegate(users))
			.then(() => {
				dispatch(getUserById())
					.then(() => {
						setLoading(false);
						setModalVisibility(!closeModal);
						if (!closeModal) {
							modalRef.current.resetUserList();
						}
					})
					.catch(() => {
						setLoading(false);
					});
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const comparer = useCallback((a, b) => {
		const name1 = `${a.firstName.trim()} ${a.lastName.trim()}`;
		const name2 = `${b.firstName.trim()} ${b.lastName.trim()}`;
		if (name1 > name2) {
			return 1;
		} else if (name1 < name2) {
			return -1;
		} else {
			return 0;
		}
	}, []);

	const onTabChange = useCallback(
		(selectedTab) => {
			setActiveTab(selectedTab);
			switch (selectedTab) {
				case '1':
					const delegatedTo = [...user.delegatedTo];
					setUsers(delegatedTo.sort(comparer));
					setNoDelegationMessage(labels.noDelegatesAdded);
					break;
				case '2':
					const delegateFrom = [...user.delegatedFrom];
					setUsers(delegateFrom.sort(comparer));
					setNoDelegationMessage(labels.noDelegationReceived);
					break;
			}
		},
		[
			user.delegatedTo,
			user.delegatedFrom,
			labels.noDelegatesAdded,
			labels.noDelegationReceived,
			comparer
		]
	);

	const removeDelegate = useCallback((toRemoveuser) => {
		setLoading(true);
		dispatch(removeDelegateUser(user.id, toRemoveuser.userId))
			.then(() => {
				dispatch(getUserById())
					.then(() => {
						setLoading(false);
					})
					.catch(() => {
						setLoading(false);
					});
			})
			.catch(() => {
				setLoading(false);
			});
	});

	const resetUsers = () => {
		let delegated = [];
		if (activeTab === '1') {
			if (Array.isArray(user.delegatedTo)) {
				delegated = [...user.delegatedTo];
			}
		} else {
			if (Array.isArray(user.delegatedFrom)) {
				delegated = [...user.delegatedFrom];
			}
		}
		setUsers(delegated.sort(comparer));
	};

	useEffect(() => {
		resetUsers();
	}, []);

	useEffect(() => {
		if (showModal) {
			resetUsers();
		}
	}, [showModal]);

	useEffect(() => {
		resetUsers();
	}, [user.delegatedTo]);

	const okButtonDisable = isLoading || isDisable || activeTab === '2';

	return (
		<Modal
			show={showModal}
			modalsize="small"
			showButtons={true}
			confirmBtnTitle={labels.save}
			closeBtnTitle={labels.closeLabel}
			title={labels.delegateAccess}
			onOkClick={() => handleSaveDelegate(false)}
			closeTitle={labels.closeLabel}
			isOkButtonDisabled={okButtonDisable}
			isCancelButtonDisabled={isLoading}
			isCancelAndCloseButtonDisabled={okButtonDisable}
			onHide={() => {
				setModalVisibility(false);
				setLoading(false);
			}}
			confirmAndcloseBtnTitle={labels.saveAndClose}
			hideConfirmAndCloseButton={false}
			onConfirmAndCloseClick={() => handleSaveDelegate(true)}
		>
			<StyledModalBody className="customScrollbar">
				<MotifProgressLoader show={isLoading} />
				<TabBar
					activeTab={activeTab}
					onTabChange={onTabChange}
					tabs={tabs}
					styleName="tab-bar"
				/>
				{activeTab === '1' ? (
					<Fragment>
						<section className="inner-modal-header">
							<p className="delegate-instruction">
								{labels.delegateToInstruction}
							</p>
						</section>
						<AddDelegate
							setLoading={setLoading}
							ref={modalRef}
							setIsDisable={setIsDisable}
						>
							{users && users.length > 0 ? (
								<ViewDelegate
									key={activeTab}
									activeTab={activeTab}
									users={users}
									removeDelegate={removeDelegate}
									noDelegationMessage={noDelegationMessage}
								/>
							) : null}
						</AddDelegate>
					</Fragment>
				) : (
					<Fragment>
						<section className="inner-modal-header">
							<p className="delegate-instruction">
								{labels.delegateFromInstruction}
							</p>
						</section>
						<ViewDelegate
							key={activeTab}
							activeTab={activeTab}
							users={users}
							removeDelegate={removeDelegate}
							noDelegationMessage={noDelegationMessage}
						/>
					</Fragment>
				)}
			</StyledModalBody>
		</Modal>
	);
}

const StyledModalBody = styled.section`
	padding: 2rem;
	min-height: 56rem;
	max-height: 56rem;
	.tab-bar {
		padding: 0;
		border: 0;
		.motif-tab-navigation-horizontal {
			border-bottom: 0;
			.motif-tab-button {
				box-shadow: none;
			}
		}
	}
	.motif-loader-container {
		background-color: ${(props) => props.theme.color[`grey50`]};
		z-index: 99;
		opacity: 0.8;
	}
	.inner-modal-header {
		.delegate-instruction {
			font-size: 1.4rem;
			margin: 1.5rem 0rem 1rem 0rem;
			line-height: 2.3rem;
		}
	}
	.show-delegates {
		margin: 3rem 0 1rem 0;
		font-size: 1.8rem;
		font-weight: bold;
		color: ${(props) => props.theme.color[`grey500`]};
	}
	.add-delegate-list {
		display: flex;
		flex-direction: column;
		.motif-button-primary {
			background: transparent;
			color: ${(props) => props.theme.color[`blue600yellow400`]};
			border: 0;
			padding: 0;
			justify-content: flex-start;
			&:hover,
			&:focus {
				background: transparent;
				color: ${(props) => props.theme.color[`blue600yellow400`]};
				border: 0;
			}

			&:active {
				box-shadow: none;
			}
		}
		.addAnotherIcon {
			margin-right: 1rem;
			.motif-icon {
				color: ${(props) => props.theme.color[`blue600yellow400`]};
				svg {
					fill: ${(props) => props.theme.color[`blue600yellow400`]};
				}
			}
		}
		.addAnotherText {
			display: inline;
			font-size: 1.5rem;
			margin-bottom: 1.5rem;
			font-weight: var(--weight-bold);
			color: ${(props) => props.theme.color[`blue400`]};
		}
		.add-element-button {
			margin-bottom: 1.5rem;
		}

		ul {
			li {
				display: flex;
				padding: 1rem 0rem 0rem 0rem;
				.userfield-row {
					.user-selectbox {
						position: relative;
						.select-dropdown {
							.react-select-container {
								.react-select__menu {
									position: relative;
									top: 0;
								}
							}
						}
						.clearall {
							display: inline-flex;
							position: absolute;
							right: 1rem;
							align-items: center;
							margin: 0;
						}
					}
				}
				.remove-delegate-button {
					height: 5rem;
					margin: 0 0 0 1rem;
					display: flex;
					align-items: center;
					justify-content: flex-start;
					svg {
						fill: ${(props) => props.theme.color[`black`]};
					}
				}
				.react-select__indicator {
					display: none;
				}
			}
		}
	}
`;

export default DelegateAccessModal;
